/**
 * Benchmark content for /benchmark, /benchmark/cuda and /benchmark/mlx.
 *
 * Rules (full text in .claude/skills/benchmark-pages/SKILL.md):
 * - Every result is relative to a named baseline ("up to N% better than …"). No absolute values — no req/s, tokens/s,
 *   milliseconds or GB — anywhere on the benchmark pages.
 * - Only improvements are shown. A metric that was the same or worse is left out of this file; `improvements()` drops anything
 *   that slips through.
 * - Each accelerator family is split into two levels: `direct` (the Fnk engine against another inference server, called directly)
 *   and `e2e` (the AI framework and backend in front of inference, driven by Gatling).
 * - The parent page never carries its own figures: it renders the `summary` gains of both families from this file.
 */

export interface Gain {
  metric: string;
  /** Improvement over the baseline, in percent. Always positive; kept at the precision of the source. */
  pct: number;
  /** Shown on the parent /benchmark page. */
  summary?: boolean;
  note?: string;
}

export interface GainGroup {
  /** The arm being measured. */
  arm: string;
  armDetail: string;
  /** The baseline it is measured against. */
  vs: string;
  vsDetail: string;
  gains: Gain[];
}

export interface BenchmarkLevel {
  title: string;
  question: string;
  lead: string;
  groups: GainGroup[];
  notes?: string[];
}

export interface BenchmarkPlatform {
  slug: 'cuda' | 'mlx';
  path: string;
  name: string;
  accelerator: string;
  model: string;
  headline: string;
  lead: string;
  direct: BenchmarkLevel;
  e2e: BenchmarkLevel;
  setup: BenchmarkSetup;
}

export interface BenchmarkSetup {
  /** Mermaid flowchart body — nodes and edges only. The direction and the site's class styles are added by `MermaidDiagram`. */
  diagram: string;
  /** Plain-language description of the diagram for screen readers. */
  diagramLabel: string;
  parameters: [string, string][];
}

/**
 * Improvements only, one row per metric, largest first. Several measurements of the same metric (latency p50/p95/p99/mean,
 * requests per second in two scenarios) collapse to the best one; the row is summarised if any of them was.
 */
export function improvements(gains: Gain[]): Gain[] {
  const best = new Map<string, Gain>();
  for (const g of gains) {
    if (g.pct <= 0) continue;
    const current = best.get(g.metric);
    const summary = Boolean(g.summary || current?.summary);
    best.set(g.metric, !current || g.pct > current.pct ? { ...g, summary } : { ...current, summary });
  }
  return [...best.values()].sort((a, b) => b.pct - a.pct);
}

/** "Up to 59% better" — the only way a result is printed. The metric name carries the direction. */
export function formatGain(g: Gain): string {
  return `Up to ${g.pct}% better`;
}

/** The gains of a level that the parent page summarises, with the baseline each is measured against. */
export function summaryGains(level: BenchmarkLevel): (Gain & { vs: string })[] {
  return level.groups.flatMap((group) => improvements(group.gains).filter((g) => g.summary).map((g) => ({ ...g, vs: group.vs })));
}

export const CUDA: BenchmarkPlatform = {
  slug: 'cuda',
  path: '/benchmark/cuda',
  name: 'NVIDIA CUDA',
  accelerator: 'NVIDIA DGX Spark, Grace Blackwell',
  model: 'Qwen3.5-9B, NVFP4',
  headline: 'Against the stack a customer runs today, the lead held at every percentile, on both chat workloads.',
  lead:
    'Two paired runs on one NVIDIA DGX Spark with the same 9B model: the Fnk engine called directly against vLLM, concurrency swept 1 → 193; and the whole backend under Gatling load, 256 concurrent chat users. The baseline is what a competent team builds today — Spring MVC + LangChain4j + vLLM. The gate required a lead of at least 20%.',
  direct: {
    title: 'Direct inference',
    question: 'How does the Fnk engine compare with vLLM on the same GPU?',
    lead:
      'The benchmark tool calls each inference server directly — the Fnk engine against vLLM, on the same GPU and model — and steps the load up from a single request.',
    groups: [
      {
        arm: 'Fnk engine',
        armDetail: 'The Fnk inference engine for NVIDIA GPUs',
        vs: 'vLLM',
        vsDetail: 'NVIDIA’s vLLM container, OpenAI-compatible API',
        gains: [
          { metric: 'Time to first token', pct: 28, summary: true }, // p95
          { metric: 'Time to first token', pct: 23 }, // p99
          { metric: 'Time to first token', pct: 9 }, // p50
          { metric: 'Requests per second', pct: 21, summary: true },
          { metric: 'Output tokens per second', pct: 21, summary: true, note: 'The lead widened with load.' },
          { metric: 'Latency', pct: 21, summary: true }, // p99
          { metric: 'Latency', pct: 18 }, // p50
          { metric: 'Latency', pct: 18 }, // p95
        ],
      },
    ],
    notes: [
      'The leads appear under load: they grow as concurrency rises, and time to first token pulls ahead once requests start to queue.',
    ],
  },
  e2e: {
    title: 'End to end',
    question: 'What does the whole stack — AI framework and engine — deliver against the assembled stack?',
    lead:
      'Gatling drives chat traffic into the backend; the backend runs the AI framework and calls inference. In the Fnk AI stack the framework and the engine work as one system, from the incoming request to the GPU.',
    groups: [
      {
        arm: 'Fnk AI stack',
        armDetail: 'Fnk framework + Fnk engine',
        vs: 'the assembled stack',
        vsDetail: 'Spring MVC + LangChain4j + vLLM',
        gains: [
          { metric: 'Latency', pct: 76.7, summary: true }, // mean, measured at the backend; also chat-full max, Gatling
          { metric: 'Latency', pct: 76.4 }, // chat-full mean, Gatling
          { metric: 'Latency', pct: 75.7 }, // chat-full p95, Gatling
          { metric: 'Latency', pct: 75.0 }, // chat-simple p50–max, Gatling
          { metric: 'Backend CPU, peak', pct: 69.3, summary: true },
          { metric: 'Backend live threads', pct: 57.0, summary: true },
        ],
      },
      {
        arm: 'Fnk framework alone',
        armDetail: 'Fnk framework, calling vLLM',
        vs: 'the assembled stack',
        vsDetail: 'Spring MVC + LangChain4j + vLLM',
        gains: [
          { metric: 'Backend live threads', pct: 58.9 },
          { metric: 'Backend CPU, peak', pct: 42.7 },
          { metric: 'Average heap', pct: 10.0 },
        ],
      },
    ],
    notes: [
      'The framework alone makes the backend markedly lighter, but on the same vLLM engine it does not deliver a clean latency gain. The large gain appears when the framework and the engine work as one system — which means owning both sides of the inference boundary.',
    ],
  },
  setup: {
    diagram: `
  test["<b>Test server</b><br/>AMD Ryzen 9 7945HX · 44 GB RAM<br/>Gatling · 256 concurrent chat users"]:::machine
  backend["<b>Backend server</b><br/>AMD Ryzen 9 7945HX · 44 GB RAM<br/>3 backends · 2 CPU / 3 GB each"]:::machine
  db[("<b>Cassandra</b><br/>1 server, on the backend server<br/>AMD Ryzen 9 7945HX · 44 GB RAM")]:::machine
  gpu["<b>NVIDIA DGX Spark</b><br/>GB10 Grace Blackwell · 128 GB unified memory<br/>Qwen3.5-9B · NVFP4<br/>Fnk engine · vLLM"]:::accent
  test -->|chat requests| backend
  backend -->|inference calls| gpu
  backend <-->|conversation history| db
  test -.->|direct inference test| gpu`,
    diagramLabel:
      'Test setup: a test server (AMD Ryzen 9 7945HX, 44 GB RAM) runs Gatling with 256 concurrent chat users against a backend server (AMD Ryzen 9 7945HX, 44 GB RAM, three backends with 2 CPUs and 3 GB each), which calls an NVIDIA DGX Spark (GB10 Grace Blackwell, 128 GB unified memory) running the Fnk engine and vLLM. The backend keeps conversation history in a single-server Cassandra that runs on the same machine as the backends. The direct inference test calls the DGX Spark without the backend.',
    parameters: [
      ['Model', 'Qwen3.5-9B, NVFP4 — the same checkpoint in vLLM and in the Fnk engine; thinking on in the direct test'],
      ['Direct inference test', 'The benchmark tool calls each inference server directly. Load rises from 1 to 193 concurrent requests in steps of 32, five minutes per step.'],
      ['End-to-end test', 'Gatling with 256 concurrent chat users: 10-second ramp-up, 60 seconds of steady load, six requests per user, answers up to 512 tokens. Two scenarios — a single question with retrieved context, and the same question inside a saved conversation.'],
      ['Three backends', 'Baseline: Spring MVC + LangChain4j calling vLLM. Fnk framework calling vLLM. Fnk AI stack: Fnk framework calling the Fnk engine. Identical CPU, memory and thread limits.'],
      ['Inference limit', 'Both inference servers take up to 128 requests at a time'],
      ['Conversation store', 'Cassandra on the backend server, shared by the three backends'],
      ['Network', 'Three separate machines — not a single-host or loopback test'],
      ['What counts', 'Successful responses only'],
    ],
  },
};

/**
 * CUDA end to end: the Fnk AI stack's latency lead over the assembled stack at each point of the response-time distribution,
 * per chat scenario, measured by Gatling. Percent better (lower latency); drawn by `LatencyLeadChart` on /benchmark/cuda.
 */
export interface LatencyLeadSeries {
  label: string;
  sub: string;
  values: number[];
}

export const CUDA_LATENCY_LEAD: { points: string[]; series: LatencyLeadSeries[] } = {
  points: ['Median', 'p75', 'p95', 'p99', 'Slowest', 'Average'],
  series: [
    { label: 'Single question', sub: 'one question with retrieved context', values: [75.0, 75.0, 75.0, 75.0, 75.0, 74.4] },
    { label: 'Saved conversation', sub: 'the same question with conversation history', values: [76.2, 75.8, 75.7, 76.3, 76.7, 76.4] },
  ],
};

export const MLX: BenchmarkPlatform = {
  slug: 'mlx',
  path: '/benchmark/mlx',
  name: 'Apple Silicon / MLX',
  accelerator: 'Apple Silicon, unified memory',
  model: 'Qwen3.5-9B, MLX 4-bit',
  headline: 'On unified memory, the same direction held — at the engine and end to end.',
  lead:
    'Two runs on Apple Silicon with the same 9B model: the Fnk MLX engine called directly against LM Studio and Ollama, concurrency swept 1 → 32; and the whole backend under Gatling load, the Fnk AI stack against a blocking backend calling LM Studio.',
  direct: {
    title: 'Direct inference',
    question: 'How does the Fnk MLX engine compare with LM Studio and Ollama on the same machine and model?',
    lead:
      'The benchmark tool calls each inference server directly — no backend, no framework — with the same model and thinking enabled.',
    groups: [
      {
        arm: 'Fnk MLX engine',
        armDetail: 'The Fnk inference engine for Apple Silicon',
        vs: 'LM Studio',
        vsDetail: 'LM Studio server, same MLX model',
        gains: [
          { metric: 'Time to first token', pct: 59, summary: true }, // p50
          { metric: 'Time to first token', pct: 54 }, // p99
          { metric: 'Time to first token', pct: 38 }, // p95
          { metric: 'Latency', pct: 27 }, // p99
          { metric: 'Latency', pct: 26, summary: true }, // p50
          { metric: 'Output tokens per second', pct: 15, summary: true },
          { metric: 'Latency', pct: 12 }, // p95
          { metric: 'Requests per second', pct: 11 },
        ],
      },
      {
        arm: 'Fnk MLX engine',
        armDetail: 'The Fnk inference engine for Apple Silicon',
        vs: 'Ollama',
        vsDetail: 'Ollama server, Ollama’s build of the same model',
        gains: [
          { metric: 'Output tokens per second', pct: 543, summary: true },
          { metric: 'Requests per second', pct: 465 },
          { metric: 'Time to first token', pct: 99, summary: true }, // p50, p95, p99
          { metric: 'Latency', pct: 96, summary: true }, // p50
          { metric: 'Latency', pct: 95 }, // p95
          { metric: 'Latency', pct: 73 }, // p99
        ],
      },
    ],
    notes: [
      'The largest leads appear under load.',
      'Ollama answered one request at a time: its throughput stayed at the single-request level as load rose, and its waits grew with the queue — so the lead over Ollama widens with every step of concurrency.',
    ],
  },
  e2e: {
    title: 'End to end',
    question: 'What does the AI framework deliver in front of inference, under real chat load?',
    lead:
      'Gatling drives two chat scenarios into the backend: a single question with retrieved context, and the same question inside a saved conversation. Three backends with identical resources and the same model; the baseline is Spring MVC + LangChain4j calling LM Studio.',
    groups: [
      {
        arm: 'Fnk AI stack',
        armDetail: 'Fnk framework + Fnk MLX engine',
        vs: 'a blocking backend on LM Studio',
        vsDetail: 'Spring MVC + LangChain4j + LM Studio',
        gains: [
          { metric: 'Requests per second', pct: 78.5, summary: true }, // peak one-minute
          { metric: 'Requests per second', pct: 20.0 }, // peak one-minute
          { metric: 'Latency', pct: 43.7, summary: true }, // mean, measured at the backend
          { metric: 'Latency', pct: 16.8 }, // mean, measured at the backend
          { metric: 'Latency', pct: 44.3 }, // p75, measured by Gatling
          { metric: 'Latency', pct: 42.6 }, // p50, measured by Gatling
          { metric: 'Backend live threads', pct: 53.1, summary: true },
          { metric: 'Peak heap', pct: 52.5 },
          { metric: 'Backend CPU, average', pct: 40, summary: true },
          { metric: 'Average heap', pct: 25.9 },
        ],
      },
      {
        arm: 'Fnk framework alone',
        armDetail: 'Fnk framework, calling LM Studio',
        vs: 'a blocking backend on LM Studio',
        vsDetail: 'Spring MVC + LangChain4j + LM Studio',
        gains: [
          { metric: 'Backend live threads', pct: 55.9 },
          { metric: 'Peak heap', pct: 53.5 },
          { metric: 'Backend CPU, average', pct: 40 },
          { metric: 'Latency', pct: 12.1 }, // p50, measured by Gatling
          { metric: 'Latency', pct: 11.3 }, // mean, measured at the backend
          { metric: 'Requests per second', pct: 7.3 }, // peak one-minute
        ],
      },
    ],
    notes: [
      'The framework alone makes the backend markedly lighter; the large requests-per-second and latency gains appear once the framework and the engine work as one system — the same pattern as on CUDA.',
    ],
  },
  setup: {
    diagram: `
  test["<b>Test server</b><br/>AMD Ryzen 9 7945HX · 44 GB RAM<br/>Gatling · 50 concurrent chat users"]:::machine
  backend["<b>Backend server</b><br/>AMD Ryzen 9 7945HX · 44 GB RAM<br/>3 backends · 4 CPU / 3 GB each"]:::machine
  db[("<b>Cassandra</b><br/>2 servers<br/>AMD Ryzen 9 7945HX · 44 GB RAM each")]:::machine
  mlx["<b>Apple Silicon (MLX)</b><br/>Qwen3.5-9B · 4-bit<br/>Fnk MLX engine · LM Studio · Ollama"]:::accent
  test -->|chat requests| backend
  backend -->|inference calls| mlx
  backend <-->|conversation history| db
  test -.->|direct inference test| mlx`,
    diagramLabel:
      'Test setup: a test server (AMD Ryzen 9 7945HX, 44 GB RAM) runs Gatling with 50 concurrent chat users against a backend server (AMD Ryzen 9 7945HX, 44 GB RAM, three backends with 4 CPUs and 3 GB each). The backend keeps conversation history in a two-server Cassandra cluster on the same AMD configuration and calls an Apple Silicon machine running the Fnk MLX engine, LM Studio and Ollama. The direct inference test calls the Apple Silicon machine without the backend.',
    parameters: [
      ['Model', 'Qwen3.5-9B, MLX 4-bit — the same in LM Studio and in the Fnk MLX engine; Ollama runs its own build of Qwen3.5-9B. Thinking on, answers up to 8,192 tokens'],
      ['Direct inference test', 'The benchmark tool calls each inference server directly — the Fnk MLX engine, LM Studio and Ollama, each in turn. Load rises from 1 to 32 concurrent requests in steps of 4, five minutes per step.'],
      ['End-to-end test', 'Gatling with 50 concurrent chat users: 60-second ramp-up, five minutes of steady load, four repeats. Two scenarios — a single question with retrieved context, and the same question inside a saved conversation.'],
      ['Three backends', 'Baseline: Spring MVC + LangChain4j calling LM Studio. Fnk framework calling LM Studio. Fnk AI stack: Fnk framework calling the Fnk MLX engine. Identical CPU, memory and thread limits.'],
      ['Conversation store', 'Cassandra on two dedicated servers, each with the same AMD Ryzen 9 7945HX and 44 GB RAM as the backend'],
    ],
  },
};

export const PLATFORMS: BenchmarkPlatform[] = [CUDA, MLX];

